import clsx from 'clsx'
import type { CSSProperties, ReactNode } from 'react'
import { Panel } from '@/components/ui/primitives.tsx'

/**
 * The administrator planes' shared chrome.
 *
 * It lived in `components/marketplace/admin/` while the agent studio was the only administrative
 * surface. PRD-21 added a second one — `/people`, which is about human beings and therefore not
 * a tab of something called "Agent studio" — and a shared dialect that lives inside one of its
 * two consumers is a dialect that drifts. Promoted here rather than copied there.
 *
 * The studio is the densest surface in the product — seven tabs of queues, forms and rows —
 * and every one of them opens the same way: a heading, one sentence saying what this part of
 * the platform decides, and sometimes a single control. Writing that nine times by hand is
 * how two tabs end up disagreeing about what a section title is, which on an admin plane
 * reads as two products bolted together rather than as one instrument.
 *
 * The serif is deliberately absent from everything here. It belongs to the one heading that
 * opens a screen — "Agent studio", "Your agents" — and a card title set in a display face
 * reads as an essay rather than as the label above a control that suspends an agent for the
 * whole company.
 *
 * `components/agents/*` — the Definitions tab — imports from here too. That tab is not a
 * neighbour of the studio, it is inside it, and letting the legacy definition editor drift
 * into its own visual dialect is exactly what this module exists to stop.
 *
 * One convention worth stating once, because it is spelled inline at every call site: a
 * surface nested *inside* a panel — a quoted claim, a diff box, a dry-run report — is washed
 * with `bg-fg/[0.02–0.03]` rather than with a surface token. The ink colour inverts with the
 * theme, so the same class recesses on warm paper and lifts on near-black; a surface token
 * picked to read on one of those disappears into the panel on the other.
 */

/** A section opener: what this part of the studio decides, and its one control if it has one. */
export const SectionHead = ({ title, lead, action }: { title: string; lead: string; action?: ReactNode }) => (
  <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
    {/* A basis, so on a phone the action wraps below rather than squeezing the lead into a
        narrow column beside it. */}
    <div className="min-w-0 flex-1 basis-[15rem]">
      <h2 className="font-sans text-[15px] font-semibold tracking-[-0.018em]">{title}</h2>
      <p className="mt-1 max-w-2xl text-pretty text-[13px] leading-[1.5] text-fg-muted">{lead}</p>
    </div>
    {action && <div className="flex shrink-0 flex-wrap items-center gap-2">{action}</div>}
  </div>
)

/**
 * One row of a queue.
 *
 * A card rather than a table row on purpose: several of these rows carry a form, a diff or a
 * lazily loaded panel, and none of that fits a cell. It also keeps every row a `listitem`,
 * which is how the browser suite addresses them.
 *
 * The hairline is the only thing that answers the pointer. The consumer cards lift because
 * the whole card is one link; an admin row holds four separate controls, and a surface that
 * moves under a cursor aiming at "Suspend" is a surface that gets misclicked.
 */
export const StudioRow = ({
  className,
  style,
  children
}: {
  className?: string
  style?: CSSProperties
  children: ReactNode
}) => (
  <Panel
    style={style}
    className={clsx('rise px-4 py-3.5 transition-colors duration-200 hover:border-rule-strong', className)}
  >
    {children}
  </Panel>
)

/**
 * The entrance stagger, capped.
 *
 * A listing index of forty rows would otherwise take two seconds to finish arriving, which
 * stops being an animation and becomes a wait.
 */
export const riseDelay = (index: number): CSSProperties => ({ animationDelay: `${Math.min(index, 7) * 45}ms` })

/**
 * A consequence box: something was refused, or is about to be lost.
 *
 * Icon plus words, never the tint alone — the alert wash is the same one the dark theme
 * re-picks, and colour is not allowed to be the only carrier of meaning (AGM-124).
 */
export const Notice = ({
  title,
  icon,
  role,
  children
}: {
  title: string
  icon?: ReactNode
  /** Only for a message that appears in response to an action the person just took. */
  role?: 'alert'
  children?: ReactNode
}) => (
  <div role={role} className="rounded-tile border border-alert/40 bg-alert-soft px-3 py-2.5">
    <p className="flex items-center gap-1.5 text-[13px] font-semibold text-alert">
      {icon}
      {title}
    </p>
    {children && <div className="mt-1 text-[12.5px] leading-relaxed text-fg-muted">{children}</div>}
  </div>
)
